import { BusinessError } from '@marketing-milk/interfaces'
import { getBusinessErrorInfo, Severity } from '../accounts/builder/error-center/severity'

// short label per flag, shared by the Facebook Connections rows and My Account so they never word one differently
export const BUSINESS_ERROR_LABELS: Record<BusinessError, string> = {
  [BusinessError.ad_account_access_error]: 'Ad account access error',
  [BusinessError.page_access_error]: 'Page access error',
  [BusinessError.lead_access_error]: 'Lead access error',
  [BusinessError.offline_upload_error]: 'Offline upload error',
  [BusinessError.google_ads_access_error]: 'Google Ads access error',
  // DRI-462: named for what it blocks, not the setup behind it
  [BusinessError.fb_system_user_mismatch]: 'Ads publish warning',
}

// shown on hover where a short label alone doesn't say what to do
export const BUSINESS_ERROR_HINTS: Partial<Record<BusinessError, string>> = {
  [BusinessError.fb_system_user_mismatch]:
    "The Ad Account and Page are on different system users, and the Ad Account's has no Advertise access on the Page, so the Ad Builder can't publish. Fix: use one system user for both.",
}

// the Error Center's split (critical blocks the ad builder, warning doesn't), so both pages classify a flag the
// same way. Severity doesn't depend on who connects the business, only the remediation steps do.
export const errorSeverity = (error: BusinessError): Severity =>
  getBusinessErrorInfo(error, false).severity

export const isWarning = (error: BusinessError) => errorSeverity(error) === 'warning'
type Flags = BusinessError[] | null | undefined

export const hasCritical = (errors: Flags) => (errors ?? []).some(e => !isWarning(e))
export const hasWarning = (errors: Flags) => (errors ?? []).some(isWarning)

// DRI-462: the Connections page lists errors and warnings apart, since a sweep can add hundreds of Ads publish
// warnings that would otherwise bury the access errors. The two filters combine: with both on, anything with an
// error or a warning shows; with neither, everything does.
export interface IssueFilter {
  errors: boolean
  warnings: boolean
}

export const NO_ISSUE_FILTER: IssueFilter = { errors: false, warnings: false }

export const isFiltering = (filter: IssueFilter) => filter.errors || filter.warnings

export const matchesIssueFilter = (errors: Flags, filter: IssueFilter) =>
  !isFiltering(filter) ||
  (filter.errors && hasCritical(errors)) ||
  (filter.warnings && hasWarning(errors))

export const severityStyle: Record<
  Severity,
  { icon: string; chipBackground: string; chipText: string }
> = {
  critical: { icon: '#f44336', chipBackground: '#FEE2E2', chipText: '#B91C1C' },
  warning: { icon: '#d97706', chipBackground: '#FEF3C7', chipText: '#92400E' },
}
